import axios from 'axios';

import { backendClient } from '../lib/api/backendClient';
import type {
  CreateWorkYoutubeMatchPayload,
  CreateWorkYoutubeMatchResponse,
  WorkYoutubeMatch,
  WorkYoutubeMatchCollectionResponse,
  YoutubeMatchSubmitter,
} from '../types/youtubeMatches';

type FetchOptions = {
  limit?: number;
};

type BackendYoutubeMatch = Record<string, unknown>;

export class YoutubeMatchesRequestError extends Error {
  code?: string;
  status?: number;
  details?: unknown;

  constructor(message: string, options: { code?: string; status?: number; details?: unknown } = {}) {
    super(message);
    this.name = 'YoutubeMatchesRequestError';
    this.code = options.code;
    this.status = options.status;
    this.details = options.details;
  }
}

const toRequestError = (error: unknown): YoutubeMatchesRequestError => {
  if (axios.isAxiosError(error)) {
    const status = error.response?.status;
    const payload = (error.response?.data ?? {}) as {
      error?: { code?: string; message?: string; details?: unknown };
    };
    const message =
      payload.error?.message ?? error.message ?? 'Unable to complete YouTube match request.';
    return new YoutubeMatchesRequestError(message, {
      code: payload.error?.code,
      status,
      details: payload.error?.details,
    });
  }

  if (error instanceof Error) {
    return new YoutubeMatchesRequestError(error.message);
  }

  return new YoutubeMatchesRequestError('Unable to complete YouTube match request.');
};

const coerceString = (value: unknown): string | undefined => {
  if (typeof value === 'string' && value.trim().length > 0) {
    return value.trim();
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value);
  }
  return undefined;
};

const coerceNumber = (value: unknown): number | undefined => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string' && value.trim().length > 0) {
    const parsed = Number(value);
    if (!Number.isNaN(parsed)) {
      return parsed;
    }
  }
  return undefined;
};

const coerceBoolean = (value: unknown, fallback: boolean): boolean => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (['true', '1', 'yes', 'on'].includes(normalized)) return true;
    if (['false', '0', 'no', 'off'].includes(normalized)) return false;
  }
  return fallback;
};

const toSubmitter = (value: unknown): YoutubeMatchSubmitter => {
  const payload = value && typeof value === 'object' ? (value as Record<string, unknown>) : {};
  return {
    id: coerceString(payload.id) ?? coerceString(payload.userId) ?? '',
    displayName:
      coerceString(payload.displayName) ??
      coerceString(payload.display_name) ??
      coerceString(payload.name) ??
      null,
    avatarUrl: coerceString(payload.avatarUrl) ?? coerceString(payload.avatar_url) ?? null,
  };
};

const toWorkYoutubeMatch = (
  payload: BackendYoutubeMatch,
  defaults: { workId?: string } = {}
): WorkYoutubeMatch => {
  const id = coerceNumber(payload.id);
  const workId =
    coerceString(payload.workId) ?? coerceString(payload.work_id) ?? defaults.workId ?? '';
  const youtubeVideoId =
    coerceString(payload.youtubeVideoId) ?? coerceString(payload.youtube_video_id) ?? '';
  const performerCredit =
    coerceString(payload.performerCredit) ?? coerceString(payload.performer_credit) ?? '';

  const rawStartMs = coerceNumber(payload.startMs) ?? coerceNumber(payload.start_ms);
  const rawEndMs = coerceNumber(payload.endMs) ?? coerceNumber(payload.end_ms);
  const legacyStartSeconds = coerceNumber(payload.start);
  const legacyEndSeconds = coerceNumber(payload.end);

  const startMs =
    typeof rawStartMs === 'number'
      ? rawStartMs
      : typeof legacyStartSeconds === 'number'
        ? legacyStartSeconds * 1000
        : 0;
  const endMs =
    typeof rawEndMs === 'number'
      ? rawEndMs
      : typeof legacyEndSeconds === 'number'
        ? legacyEndSeconds * 1000
        : null;

  const notes = coerceString(payload.notes) ?? null;
  const createdAt =
    coerceString(payload.createdAt) ??
    coerceString(payload.created_at) ??
    new Date().toISOString();
  const submitter = toSubmitter(payload.submitter);
  const isMine = coerceBoolean(payload.isMine, false);

  return {
    id: typeof id === 'number' ? id : -1,
    workId,
    youtubeVideoId,
    performerCredit,
    startMs,
    endMs,
    notes,
    createdAt,
    submitter,
    isMine,
  };
};

const sanitizeLimit = (limit?: number): number | undefined => {
  if (typeof limit !== 'number' || !Number.isFinite(limit)) {
    return undefined;
  }
  const normalized = Math.floor(limit);
  return normalized > 0 ? normalized : undefined;
};

export const fetchWorkYoutubeMatches = async (
  workId: string,
  options: FetchOptions = {}
): Promise<WorkYoutubeMatchCollectionResponse> => {
  const trimmedId = typeof workId === 'string' ? workId.trim() : '';
  if (!trimmedId) {
    throw new YoutubeMatchesRequestError('A work identifier is required.');
  }

  try {
    const params: Record<string, number> = {};
    const limit = sanitizeLimit(options.limit);
    if (typeof limit === 'number') {
      params.limit = limit;
    }

    const response = await backendClient.get<{ matches?: unknown; total?: unknown }>(
      `/works/${encodeURIComponent(trimmedId)}/youtube-matches`,
      Object.keys(params).length ? { params } : undefined
    );

    const matchesPayload = Array.isArray(response.data?.matches) ? response.data.matches : [];
    const matches = matchesPayload.map((item) =>
      toWorkYoutubeMatch(item as BackendYoutubeMatch, { workId: trimmedId })
    );
    const totalValue = coerceNumber(response.data?.total);

    return {
      matches,
      total:
        typeof totalValue === 'number' && Number.isFinite(totalValue) ? totalValue : matches.length,
    };
  } catch (error) {
    throw toRequestError(error);
  }
};

export const createWorkYoutubeMatch = async (
  workId: string,
  payload: CreateWorkYoutubeMatchPayload
): Promise<CreateWorkYoutubeMatchResponse> => {
  const trimmedId = typeof workId === 'string' ? workId.trim() : '';
  if (!trimmedId) {
    throw new YoutubeMatchesRequestError('A work identifier is required.');
  }

  const performerCredit =
    typeof payload.performerCredit === 'string' ? payload.performerCredit.trim() : '';
  if (!performerCredit.length) {
    throw new YoutubeMatchesRequestError('Performer credit is required.');
  }

  const youtubeUrl =
    typeof payload.youtubeUrl === 'string' && payload.youtubeUrl.trim().length
      ? payload.youtubeUrl.trim()
      : undefined;
  const youtubeVideoId =
    typeof payload.youtubeVideoId === 'string' && payload.youtubeVideoId.trim().length
      ? payload.youtubeVideoId.trim()
      : undefined;

  if (!youtubeUrl && !youtubeVideoId) {
    throw new YoutubeMatchesRequestError('Provide a YouTube URL or video id.');
  }

  const requestBody: Record<string, unknown> = { performerCredit };
  if (youtubeUrl) requestBody.youtubeUrl = youtubeUrl;
  if (youtubeVideoId) requestBody.youtubeVideoId = youtubeVideoId;

  if (payload.start !== undefined && payload.start !== null && payload.start !== '') {
    requestBody.start = payload.start;
  }
  if (payload.end !== undefined && payload.end !== null && payload.end !== '') {
    requestBody.end = payload.end;
  }
  if (typeof payload.notes === 'string' && payload.notes.trim().length) {
    requestBody.notes = payload.notes.trim();
  }

  try {
    const response = await backendClient.post<{ created?: unknown; match?: BackendYoutubeMatch }>(
      `/works/${encodeURIComponent(trimmedId)}/youtube-matches`,
      requestBody
    );
    const created = coerceBoolean(response.data?.created, true);
    const match = toWorkYoutubeMatch(response.data?.match ?? {}, { workId: trimmedId });
    return { created, match };
  } catch (error) {
    throw toRequestError(error);
  }
};

export const deleteWorkYoutubeMatch = async (matchId: number): Promise<void> => {
  if (!Number.isInteger(matchId) || matchId <= 0) {
    throw new YoutubeMatchesRequestError('A positive match id is required.');
  }
  try {
    await backendClient.delete(`/youtube-matches/${matchId}`);
  } catch (error) {
    throw toRequestError(error);
  }
};

export const youtubeMatchesService = {
  fetchWorkYoutubeMatches,
  createWorkYoutubeMatch,
  deleteWorkYoutubeMatch,
};

export type {
  CreateWorkYoutubeMatchPayload,
  CreateWorkYoutubeMatchResponse,
  WorkYoutubeMatch,
  WorkYoutubeMatchCollectionResponse,
};

